import React, { useState } from "react";
import Logo from "shared/Logo/Logo";
import { NavLink, Link } from "react-router-dom";
import { NavItemType } from "./NavigationItem";
import { NAVIGATION_NEW } from "data/navigation";
import { ChevronDownIcon, XMarkIcon } from "@heroicons/react/24/outline";

export interface NavMobileProps {
  data?: NavItemType[];
  onClickClose?: () => void;
}

const NavMobile: React.FC<NavMobileProps> = ({
  data = NAVIGATION_NEW,
  onClickClose,
}) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const toggleExpand = (id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center justify-between px-4 py-3.5 rounded-xl text-[15px] font-medium transition-all ${
      isActive
        ? "bg-white/10 text-white"
        : "text-white/75 hover:bg-white/5 hover:text-white"
    }`;

  const renderSubLinks = (items: NavItemType[]) => (
    <ul className="mt-1 mb-2 ml-2 pl-3 border-l border-white/10 space-y-0.5">
      {items.map((child) => (
        <li key={child.id}>
          <NavLink
            end
            to={child.href || "/"}
            onClick={onClickClose}
            className={({ isActive }) =>
              `block px-3 py-2.5 rounded-lg text-sm transition-colors ${
                isActive
                  ? "text-white bg-white/10"
                  : "text-white/55 hover:text-white hover:bg-white/5"
              }`
            }
          >
            {child.name}
          </NavLink>
        </li>
      ))}
    </ul>
  );

  const renderMegaCategories = (categories: NavItemType["megaMenu"]) => (
    <ul className="mt-1 mb-2 ml-2 pl-3 border-l border-white/10 space-y-0.5 max-h-52 overflow-y-auto">
      {categories?.map((cat) => (
        <li key={cat.id}>
          <NavLink
            to={`/categories/${cat.id}`}
            onClick={onClickClose}
            className={({ isActive }) =>
              `block px-3 py-2.5 rounded-lg text-sm transition-colors ${
                isActive
                  ? "text-white bg-white/10"
                  : "text-white/55 hover:text-white hover:bg-white/5"
              }`
            }
          >
            {cat.title}
          </NavLink>
        </li>
      ))}
    </ul>
  );

  const renderNavItem = (item: NavItemType) => {
    const hasDropdown =
      item.type === "dropdown" && item.children && item.children.length > 0;
    const hasMega =
      item.type === "megaMenu" && item.megaMenu && item.megaMenu.length > 0;
    const isExpandable = hasDropdown || hasMega;
    const isOpen = expandedId === item.id;

    if (!isExpandable) {
      return (
        <li key={item.id}>
          <NavLink
            end
            to={item.href || "/"}
            onClick={onClickClose}
            className={linkClass}
          >
            {item.name}
          </NavLink>
        </li>
      );
    }

    return (
      <li key={item.id}>
        <button
          type="button"
          onClick={() => toggleExpand(item.id)}
          className={`w-full flex items-center justify-between px-4 py-3.5 rounded-xl text-[15px] font-medium transition-all ${
            isOpen
              ? "bg-white/10 text-white"
              : "text-white/75 hover:bg-white/5 hover:text-white"
          }`}
        >
          <span>{item.name}</span>
          <ChevronDownIcon
            className={`w-4 h-4 transition-transform duration-200 ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </button>
        {isOpen && (
          <div className="overflow-hidden">
            {hasDropdown && renderSubLinks(item.children!)}
            {hasMega && renderMegaCategories(item.megaMenu)}
          </div>
        )}
      </li>
    );
  };

  return (
    <div className="flex flex-col h-full min-h-screen bg-[#0f1b3d] shadow-2xl">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-5 border-b border-white/10">
        <Logo className="w-20" />
        <button
          onClick={onClickClose}
          className="w-10 h-10 flex items-center justify-center rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-colors"
          aria-label="Close menu"
        >
          <XMarkIcon className="w-6 h-6" />
        </button>
      </div>

      {/* Nav links */}
      <nav className="flex-1 overflow-y-auto px-4 py-5">
        <p className="px-4 mb-3 text-[11px] font-semibold tracking-widest uppercase text-white/35">
          Navigation
        </p>
        <ul className="space-y-1">{data.map(renderNavItem)}</ul>
      </nav>

      {/* Footer */}
      <div className="px-5 py-6 border-t border-white/10 space-y-5 bg-[#0a1428]/60">
        <Link
          to="/contact"
          onClick={onClickClose}
          className="block w-full text-center bcr-btn-primary py-3.5 text-sm font-semibold"
        >
          Get a Quote
        </Link>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {[
              { icon: "lab la-facebook-square", href: "https://www.facebook.com/people/BCR-Ltd-Expansion-Joint-UK/100064174416066/" },
              { icon: "lab la-instagram", href: "https://www.instagram.com/bcr_profiles/" },
              { icon: "lab la-youtube", href: "https://www.youtube.com/channel/UC37h6R6_AODVk5hbxZ86-qQ" },
              { icon: "lab la-linkedin-in", href: "https://www.linkedin.com/company/shadow-gap-profiles/" },
            ].map((s, i) => (
              <a
                key={i}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-lg text-white/50 hover:text-white transition-colors"
              >
                <i className={s.icon} />
              </a>
            ))}
          </div>
          <a
            href="tel:+971506454326"
            className="text-sm text-white/60 hover:text-white transition-colors"
          >
            +971 506 454 326
          </a>
        </div>
      </div>
    </div>
  );
};

export default NavMobile;
